/**
 * Helpers for dynamic field parent dependencies (schema.params).
 * e.g. Folder params: [{ name: "space_id" }] — Folder is disabled until Space has a value.
 * Also helpers for perform-generated nested body fields (e.g. Sheets columns under results).
 */

export function unwrapFieldValue(value) {
  let v = value;
  while (
    v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.prototype.hasOwnProperty.call(v, 'value')
  ) {
    v = v.value;
  }
  return v;
}

export function isFieldValuePresent(value) {
  const v = unwrapFieldValue(value);
  if (v == null || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** Resolve a param value from top-level fields or nested body object. */
export function resolveParamValueFromFields(fields, paramName) {
  if (!Array.isArray(fields) || !paramName) return undefined;
  const top = fields.find((f) => f?.name === paramName);
  if (top && isFieldValuePresent(top.value)) return unwrapFieldValue(top.value);

  const body = fields.find((f) => f?.name === 'body' || f?.in === 'formData');
  if (body?.value && typeof body.value === 'object' && !Array.isArray(body.value)) {
    if (isFieldValuePresent(body.value[paramName])) {
      return unwrapFieldValue(body.value[paramName]);
    }
  }
  return undefined;
}

/** Human label for a dependency param (prefer sibling field title). */
export function getParamDisplayLabel(paramName, fields) {
  if (!paramName) return '';
  const top = (fields || []).find((f) => f?.name === paramName);
  if (top?.title) return top.title;
  const body = (fields || []).find((f) => f?.name === 'body' && f?.schema?.properties?.[paramName]);
  const propTitle = body?.schema?.properties?.[paramName]?.title;
  if (propTitle) return propTitle;
  return String(paramName)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @param {object} fieldOrSchema - field with .schema.params (+ optional .name), or a schema object with .params
 * @param {object[]} fields - node fields array
 * @param {string} [selfFieldName] - current field name; params matching this are ignored (API params often include self)
 * @returns {Array<{ name: string, label: string }>}
 */
export function getUnmetDynamicParams(fieldOrSchema, fields, selfFieldName) {
  const schema = fieldOrSchema?.schema || fieldOrSchema;
  const rawSelf =
    selfFieldName ||
    (fieldOrSchema && fieldOrSchema.schema && fieldOrSchema.name != null
      ? fieldOrSchema.name
      : undefined);
  const selfName =
    rawSelf != null && String(rawSelf).trim() !== '' && String(rawSelf) !== 'root'
      ? String(rawSelf)
      : undefined;
  const params = schema?.params;
  if (!Array.isArray(params) || params.length === 0) return [];
  return params
    .filter((p) => {
      if (!p?.name) return false;
      // e.g. Google Sheets "Sheet" lists params: spreadsheet + sheet — sheet is this field, not a parent
      if (selfName != null && String(p.name) === selfName) return false;
      // When edited as RJSF root (id "root"), name may be missing — treat matching dynamic schema as self
      if (selfName == null && schema?.dynamic && Array.isArray(fields)) {
        const match = fields.find((f) => f?.name === p.name);
        if (match?.schema?.dynamic && match.schema.dynamic === schema.dynamic) {
          return false;
        }
      }
      return resolveParamValueFromFields(fields, p.name) === undefined;
    })
    .map((p) => ({
      name: p.name,
      label: getParamDisplayLabel(p.name, fields),
    }));
}

export function hasUnmetDynamicDependencies(fieldOrSchema, fields, selfFieldName) {
  return getUnmetDynamicParams(fieldOrSchema, fields, selfFieldName).length > 0;
}

export function getNestedValue(obj, path) {
  if (!Array.isArray(path) || path.length === 0) return obj;
  let cur = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
}

export function setNestedValue(obj, path, value) {
  if (!Array.isArray(path) || path.length === 0) return value;
  const root = obj && typeof obj === 'object' && !Array.isArray(obj) ? { ...obj } : {};
  let cur = root;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    const child = cur[key];
    cur[key] = child && typeof child === 'object' && !Array.isArray(child) ? { ...child } : {};
    cur = cur[key];
  }
  cur[path[path.length - 1]] = value;
  return root;
}

export function getSchemaAtPath(rootSchema, path) {
  if (!rootSchema || !Array.isArray(path) || path.length === 0) return rootSchema;
  let schema = rootSchema;
  for (const key of path) {
    schema = schema?.properties?.[key];
    if (!schema) return null;
  }
  return schema;
}

function countSchemaProperties(schema) {
  const props = schema?.properties;
  if (!props || typeof props !== 'object') return 0;
  let n = 0;
  Object.keys(props).forEach((k) => {
    if (k === '') return;
    const child = props[k];
    if (child?.type === 'object' && child.properties) {
      n += countSchemaProperties(child);
    } else {
      n += 1;
    }
  });
  return n;
}

/**
 * When a field update races with perform(), keep the richer body schema from Redux.
 */
export function mergeFieldsPreferRicherBody(reduxFields, incomingFields) {
  if (!Array.isArray(incomingFields)) return incomingFields;
  if (!Array.isArray(reduxFields)) return incomingFields;
  const reduxBody = reduxFields.find((f) => f?.name === 'body' || f?.in === 'formData');
  const incBodyIdx = incomingFields.findIndex((f) => f?.name === 'body' || f?.in === 'formData');
  if (!reduxBody?.schema?.properties || incBodyIdx < 0) return incomingFields;
  const incBody = incomingFields[incBodyIdx];
  const reduxCount = countSchemaProperties(reduxBody.schema);
  const incCount = countSchemaProperties(incBody?.schema);
  if (reduxCount <= incCount) return incomingFields;
  const next = [...incomingFields];
  const reduxVal =
    reduxBody.value && typeof reduxBody.value === 'object' && !Array.isArray(reduxBody.value)
      ? reduxBody.value
      : {};
  const incVal =
    incBody?.value && typeof incBody.value === 'object' && !Array.isArray(incBody.value)
      ? incBody.value
      : {};
  next[incBodyIdx] = {
    ...incBody,
    schema: reduxBody.schema,
    value: { ...reduxVal, ...incVal },
  };
  return next;
}

/**
 * Build a synthetic field object for a body property row (override UI).
 */
export function getOverrideRowField(fields, row) {
  if (!row || !Array.isArray(fields)) return null;
  const f = fields[row.fieldIndex];
  if (!f) return null;
  const path =
    Array.isArray(row.bodyPath) && row.bodyPath.length
      ? row.bodyPath
      : row.bodyKey != null
        ? [row.bodyKey]
        : null;
  if (path) {
    const leafSchema = getSchemaAtPath(f.schema, path);
    if (leafSchema) {
      return {
        name: path[path.length - 1],
        title: row.title || leafSchema.title || path[path.length - 1],
        schema: leafSchema,
      };
    }
  }
  return f;
}
